import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Divider,
  LinearProgress,
  Tooltip,
  IconButton
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

/**
 * Reusable data card component for displaying metrics and statistics
 * 
 * @param {Object} props
 * @param {string} props.title - Card title
 * @param {string|number} props.value - Primary value to display
 * @param {string} props.unit - Optional unit for the value (%, kg, etc)
 * @param {string} props.subtitle - Optional subtitle or description
 * @param {React.ReactNode} props.icon - Optional icon to display
 * @param {string} props.color - Optional color for the icon background (primary, secondary, success, warning, error, info)
 * @param {number} props.progress - Optional progress value (0-100)
 * @param {string} props.tooltip - Optional tooltip text
 * @param {React.ReactNode} props.action - Optional action button or component
 * @param {React.ReactNode} props.footer - Optional footer content
 * @param {Object} props.sx - Optional additional styles
 */
const DataCard = ({ 
  title, 
  value, 
  unit, 
  subtitle, 
  icon, 
  color = 'primary', 
  progress, 
  tooltip,
  action,
  footer,
  sx = {} 
}) => {
  return (
    <Card 
      elevation={1} 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        ...sx
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {title}
            {tooltip && (
              <Tooltip title={tooltip} arrow placement="top">
                <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                  <InfoIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Typography>
          {action && (
            <Box>
              {action}
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {icon && (
            <Box 
              sx={{ 
                mr: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                p: 1,
                bgcolor: `${color}.light`,
                color: `${color}.main`
              }}
            >
              {icon}
            </Box>
          )}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
              <Typography variant="h4" component="span" fontWeight="medium">
                {value}
              </Typography>
              {unit && (
                <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                  {unit}
                </Typography>
              )}
            </Box>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        
        {progress !== undefined && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              color={color} 
              sx={{ height: 6, borderRadius: 1 }}
            />
          </Box>
        )}
      </CardContent>
      
      {footer && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            {footer}
          </Box>
        </>
      )}
    </Card>
  );
};

export default DataCard;
